import React, { useEffect } from 'react';
import { useRecordContext } from 'react-admin';
import { format, addMonths, addWeeks, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import uk from 'date-fns/locale/uk';

import axiosApiInstance from 'src/api/axiosConfig';
import { EmployeeFormFields } from 'src/constants/employee';
import EmployeeReportToolbar from '../../form/EmployeeReportToolbar';
import { LessonsSection } from './LessonReport';
import { FORMAT_DATE_STRING } from 'src/constants';

interface LessonCount {
    groupCount: number;
    individualCount: number;
    totalCount: number;
}

/**
 * Renders the Employee Report Tab, allowing users to view
 * lessons provided by an employee in a specified time range (month, week, or all)
 */
export const EmployeeReportTab: React.FC<{ selectedCenterId: string }> = ({ selectedCenterId }) => {
    const record = useRecordContext();
    const [currentDate, setCurrentDate] = React.useState(new Date());
    const [startDate, setStartDate] = React.useState<Date | undefined>(new Date());
    const [endDate, setEndDate] = React.useState<Date | undefined>(new Date());
    const [staffLessons, setStaffLessons] = React.useState<Record<string, LessonCountRecordWithCenter>>();
    const [civilianLawLessons, setCivilianLawLessons] = React.useState<Record<string, LessonCountRecordWithCenter>>();
    const [view, setView] = React.useState('month');

    const employees = record[EmployeeFormFields.EMPLOYEES_CENTERS] || [];
    const currentEmployeeCenter = employees.find((employee: any) => {
        return employee.educationCenterId == selectedCenterId;
    });
    type LessonBreakdown = {
        groupCount: number;
        individualCount: number;
        totalCount: number;
    };
    type LessonCountRecord = {
        generalCount: LessonCount;
        lessonNamesToCount: Record<string, LessonBreakdown>;
    };
    type LessonCountRecordWithCenter = LessonCountRecord & {
        educationCenterId: string;
    };
    const transformToLessonCountRecord = (
        data: {
            educationCenterId: string | number;
            employments: {
                employeeId: number;
                reporting: LessonCountRecord;
            }[];
        }[]
    ): Record<string, LessonCountRecordWithCenter> => {
        const result: Record<string, LessonCountRecordWithCenter> = {};
        data.forEach((entry) => {
            entry.employments.forEach((emp) => {
                result[emp.employeeId.toString()] = {
                    ...emp.reporting,
                    educationCenterId: String(entry.educationCenterId)
                };
            });
        });
        return result;
    };

    // Set the date range when the component mounts and when the view changes
    useEffect(() => {
        setDateRange(currentDate, view);
    }, [currentDate, view]);

    const setDateRange = (currentDate: Date, view: string) => {
        if (view === 'month') {
            setStartDate(startOfMonth(currentDate));
            setEndDate(endOfMonth(currentDate));
        } else if (view === 'week') {
            setStartDate(startOfWeek(currentDate, { locale: uk }));
            setEndDate(endOfWeek(currentDate, { locale: uk }));
        } else {
            setStartDate(fullRangeDates.earliest);
            setEndDate(fullRangeDates.latest);
        }
    };

    // Effect for data fetching that triggers when startDate/endDate changes
    useEffect(() => {
        if (currentEmployeeCenter) {
            let url = `employees/reporting`;

            if (startDate !== undefined && endDate !== undefined) {
                const formattedStartDate = format(new Date(startDate), FORMAT_DATE_STRING);
                const formattedEndDate = format(new Date(endDate), FORMAT_DATE_STRING);

                url += `?userId=${record.id}&startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
            }

            axiosApiInstance
                .post(url)
                .then((res) => {
                    setStaffLessons(transformToLessonCountRecord(res.data.staff));
                    setCivilianLawLessons(transformToLessonCountRecord(res.data.civilianLawNature));
                })
                .catch((e) => {
                    throw e;
                });
        }
    }, [startDate, endDate, currentEmployeeCenter, record.id, view]);

    /**
     * Handles view change events
     * @param { string } newView - new view to switch to ('month', 'week', or 'all')
     */
    const handleViewChange = (newView: string) => {
        setView(newView);
        setDateRange(currentDate, newView);
    };

    /**
     * Handles navigation events (previous, next, today)
     * @param { string } action - navigation action ('PREVIOUS', 'NEXT', or 'TODAY')
     */
    const handleNavigate = (action: string) => {
        let newDate = currentDate;

        if (view === 'month') {
            if (action === 'PREVIOUS') newDate = addMonths(currentDate, -1);
            if (action === 'NEXT') newDate = addMonths(currentDate, 1);
            if (action === 'TODAY') newDate = new Date();
        } else if (view === 'week') {
            if (action === 'PREVIOUS') newDate = addWeeks(currentDate, -1);
            if (action === 'NEXT') newDate = addWeeks(currentDate, 1);
            if (action === 'TODAY') newDate = new Date();
        }

        setCurrentDate(newDate);
    };

    // Effect for updating the date range when the "Today" button is clicked
    useEffect(() => {
        if (view !== 'all') {
            setDateRange(currentDate, view);
        }
    }, [currentDate, view]);

    /**
     * Gets the label for the current date range
     */
    const getLabel = () => {
        if (view === 'month') {
            const monthLabel = format(currentDate, 'LLLL yyyy', { locale: uk });
            return monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);
        } else if (view === 'week') {
            return `${startDate && format(startDate, 'd MMMM', { locale: uk })} - ${endDate && format(endDate, 'd MMMM yyyy', { locale: uk })}`;
        } else if (view === 'all') {
            return 'За весь період';
        } else {
            return '';
        }
    };
    type Item = {
        dateOfStarting: string;
        dateOfEnding: string;
    };
    function getFullRangeDates(arr: Item[]): { earliest: Date; latest: Date } {
        return arr.reduce(
            (acc, item) => {
                const start = new Date(item.dateOfStarting);
                const end = new Date(item.dateOfEnding);
                if (start < acc.earliest) acc.earliest = start;
                if (end > acc.earliest) acc.latest = end;
                return acc;
            },
            {
                earliest: new Date(arr[0].dateOfStarting),
                latest: new Date(arr[0].dateOfEnding)
            }
        );
    }
    const centers = record.employeeCenters.map((c: any) => ({
        dateOfStarting: c.civilLawNatureContract && c.dateOfStartingCLNC ? c.dateOfStartingCLNC : c.dateOfStarting,
        dateOfEnding: c.dateOfEnding ? c.dateOfEnding : format(new Date(), FORMAT_DATE_STRING)
    }));
    const fullRangeDates = getFullRangeDates(centers);

    return (
        <>
            <EmployeeReportToolbar
                date={currentDate}
                view={view}
                label={getLabel()}
                handleViewChange={handleViewChange}
                handleNavigate={handleNavigate}
            />
            {staffLessons &&
                Object.entries(staffLessons)
                    .filter(([, data]) => data.educationCenterId === selectedCenterId)
                    .map(([employeeId, data]) => (
                        <LessonsSection
                            key={employeeId}
                            title="Надано освітніх послуг (штатно)"
                            reportType={'EMPLOYEE_REPORT'}
                            generalCount={data.generalCount}
                            lessonNamesToCount={data.lessonNamesToCount}
                            employeeId={employeeId}
                            startDate={startDate}
                            endDate={endDate}
                        />
                    ))}
            {civilianLawLessons &&
                Object.entries(civilianLawLessons)
                    .filter(([, data]) => data.educationCenterId === selectedCenterId)
                    .map(([employeeId, data]) => (
                        <LessonsSection
                            key={employeeId}
                            title="Надано освітніх послуг (ЦПХ)"
                            reportType={'EMPLOYEE_REPORT'}
                            generalCount={data.generalCount}
                            lessonNamesToCount={data.lessonNamesToCount}
                            employeeId={employeeId}
                            startDate={startDate}
                            endDate={endDate}
                        />
                    ))}
        </>
    );
};

export default EmployeeReportTab;
